import React, { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { SlidePanel } from "./panelChrome";
import { colourClasses } from "@/lib/colours";
import { SHIFT_STATUS_ORDER, SHIFT_STATUS_META } from "@/lib/shiftStatus";
import {
  X, ChevronDown, ChevronRight, ChevronLeft, Loader2, Search, Lock, LockOpen,
  Settings, KeyRound, Sparkles,
} from "lucide-react";
import SchedulerKey from "./SchedulerKey";
import ShiftPatternsManager from "./ShiftPatternsManager";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";

const toISO = (d) => new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString().slice(0, 10);

const selCls = "w-full appearance-none rounded-lg border border-slate-300 bg-slate-50 px-3 py-2 pr-8 text-sm text-slate-900 focus:border-indigo-400 focus:ring-2 focus:ring-indigo-100 focus:outline-none";
const HOURS = Array.from({ length: 24 }, (_, i) => i);
const fmtDdmmyyyy = (iso) => (iso ? new Date(iso + "T00:00:00").toLocaleDateString("en-GB") : "");
const toMins = (t) => { const [h, m] = String(t || "0:0").split(":").map(Number); return h * 60 + m; };
const hhmm = (m) => `${String(Math.floor(m / 60)).padStart(2, "0")}:${String(m % 60).padStart(2, "0")}`;

function Chevron({ open }) { return open ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />; }

function Collapsible({ title, children, defaultOpen = false, testId }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="rounded-xl border border-slate-200">
      <button type="button" onClick={() => setOpen((o) => !o)} data-testid={testId}
        className={`flex w-full items-center justify-between px-4 py-3 text-sm font-medium text-slate-700 ${open ? "bg-indigo-50/50" : ""}`}>
        {title}<Chevron open={open} />
      </button>
      {open && <div className="px-4 py-3 space-y-3 border-t border-slate-100">{children}</div>}
    </div>
  );
}

function UsersPicker({ options, value, onChange }) {
  const has = (n) => value.includes(n);
  return (
    <div className="rounded-lg border border-slate-300 bg-slate-50 p-2">
      {value.length > 0 && (
        <div className="flex flex-wrap gap-1 mb-2">
          {value.map((n) => (
            <span key={n} className="inline-flex items-center gap-1 text-xs bg-purple-100 text-purple-700 rounded-md pl-2 pr-1 py-1 font-medium uppercase">
              {n}<button onClick={() => onChange(value.filter((v) => v !== n))} className="hover:text-rose-600"><X className="w-3 h-3" /></button>
            </span>
          ))}
        </div>
      )}
      <div className="relative">
        <select value="" onChange={(e) => { if (e.target.value && !has(e.target.value)) onChange([...value, e.target.value]); e.target.value = ""; }} className={selCls} data-testid="shift-users-select">
          <option value="">Please select…</option>
          {options.filter((o) => !has(o.name)).map((o) => <option key={o.name} value={o.name}>{o.name}</option>)}
        </select>
        <ChevronDown className="pointer-events-none absolute right-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
      </div>
    </div>
  );
}

// Basic-mode interactive hourly timeline (click to place a shift)
function TimelineRow({ label, sub, existing, placement, onPlace, step = 5 }) {
  const pct = (m) => `${(m / 1440) * 100}%`;
  const onClick = (e) => {
    if (!onPlace) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const rel = Math.min(1, Math.max(0, (e.clientX - rect.left) / rect.width));
    onPlace(Math.round((rel * 1440) / step) * step);
  };
  return (
    <div className="flex items-center border-t border-slate-100">
      <div className="w-40 shrink-0 px-3 py-2">
        <div className="text-sm font-medium text-slate-700 truncate">{label}</div>
        {sub && <div className="text-[11px] text-slate-400 truncate">{sub}</div>}
      </div>
      <div className="relative flex-1 h-12 cursor-crosshair" onClick={onClick} data-testid="timeline-track">
        {HOURS.map((h) => <span key={h} className="absolute top-0 bottom-0 w-px bg-slate-100" style={{ left: pct(h * 60) }} />)}
        {(existing || []).map((s) => {
          const c = colourClasses(SHIFT_STATUS_META[s.shift_status]?.colour || "slate");
          return <div key={s.shift_id} title={`${s.start_time}–${s.end_time}`}
            className={`absolute top-2 bottom-2 rounded ${c.bg} border ${c.border}`}
            style={{ left: pct(toMins(s.start_time)), width: pct(Math.max(15, s.duration_mins)) }} />;
        })}
        {placement && <div className="absolute top-1.5 bottom-1.5 rounded bg-indigo-500/80 border border-indigo-600 ring-2 ring-indigo-200"
          style={{ left: pct(placement.start), width: pct(placement.duration) }} data-testid="timeline-placement" />}
      </div>
    </div>
  );
}

const HOUR_LABELS = ["12am", "2am", "4am", "6am", "8am", "10am", "12pm", "2pm", "4pm", "6pm", "8pm", "10pm", "12am"];

export default function ShiftForm({ shift, initial, onClose, onSaved }) {
  const editing = !!shift;
  const [meta, setMeta] = useState(null);
  const [tab, setTab] = useState(editing ? "advanced" : "basic");
  const [assignType, setAssignType] = useState("asset"); // asset | crew
  const [step, setStep] = useState(5);
  const [travelCalc, setTravelCalc] = useState("append");
  const [travelExp, setTravelExp] = useState("expand");
  const [showKey, setShowKey] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [showPatterns, setShowPatterns] = useState(false);
  const [busy, setBusy] = useState(false);

  // shared fields
  const [date, setDate] = useState(shift?.date || initial?.date || new Date().toISOString().slice(0, 10));
  const [workType, setWorkType] = useState(shift?.work_type || "");
  const [duration, setDuration] = useState(shift?.duration_mins || 60);
  const [filter, setFilter] = useState("");

  // advanced fields
  const [vehicle, setVehicle] = useState(shift?.vehicle_legacy_id || initial?.vehicle_legacy_id || "");
  const [crewId, setCrewId] = useState(shift?.crew_id || "");
  const [users, setUsers] = useState(shift?.staff || []);
  const [shiftStatus, setShiftStatus] = useState(shift?.shift_status || "upcoming");
  const [startTime, setStartTime] = useState(shift?.start_time || "09:00");
  const [endTime, setEndTime] = useState(shift?.end_time || "11:00");
  const [notify, setNotify] = useState(!!shift?.customer_notify);
  const [onSiteHrs, setOnSiteHrs] = useState(shift?.chargeable_on_site_hours || "");
  const [travelHrs, setTravelHrs] = useState(shift?.chargeable_travel_hours || "");
  const [infra, setInfra] = useState(shift?.infrastructure_asset || "");
  const [addInfo, setAddInfo] = useState(shift?.additional_info || "");
  const [patternName, setPatternName] = useState(shift?.shift_pattern || "");

  // Link to an existing job/task (backend stores task_legacy_id on the shift)
  const [linkedTask, setLinkedTask] = useState(
    shift?.task_legacy_id ? { legacy_id: shift.task_legacy_id, title: shift.task_title || "" }
      : (initial?.task_legacy_id ? { legacy_id: initial.task_legacy_id, title: initial.task_title || "" } : null));
  const [jobQuery, setJobQuery] = useState("");
  const [jobResults, setJobResults] = useState([]);
  const [jobSearching, setJobSearching] = useState(false);

  // basic-mode placement
  const [placement, setPlacement] = useState(null); // {vehicle_legacy_id, start, duration}

  useEffect(() => { api.get("/scheduler/meta").then((r) => setMeta(r.data)).catch(() => {}); }, []);
  useEffect(() => {
    api.get("/me/scheduler-prefs").then((r) => {
      const p = r.data || {};
      if (p.step_interval) setStep(p.step_interval);
      if (p.travel_calc) setTravelCalc(p.travel_calc);
      if (p.travel_expansion) setTravelExp(p.travel_expansion);
      if (p.assign_type && !editing) setAssignType(p.assign_type);
    }).catch(() => {});
  }, []);

  const saveSchedulerPrefs = () => {
    api.put("/me/scheduler-prefs", {
      step_interval: step, travel_calc: travelCalc, travel_expansion: travelExp, assign_type: assignType,
    }).then(() => toast.success("Assignment settings saved")).catch(() => toast.error("Could not save settings"));
  };
  const [dayShifts, setDayShifts] = useState([]);
  useEffect(() => {
    api.get("/scheduler", { params: { start: date, view: "week" } })
      .then((r) => setDayShifts((r.data.shifts || []).filter((s) => s.date === date))).catch(() => {});
  }, [date]);

  useEffect(() => {
    if (linkedTask || jobQuery.trim().length < 2) { setJobResults([]); return; }
    const t = setTimeout(() => {
      setJobSearching(true);
      api.get("/tasks", { params: { q: jobQuery, page_size: 8 } })
        .then((r) => setJobResults(r.data.items || [])).catch(() => setJobResults([]))
        .finally(() => setJobSearching(false));
    }, 300);
    return () => clearTimeout(t);
  }, [jobQuery, linkedTask]);

  const assets = useMemo(() => (meta?.assets || []).filter((a) =>
    !filter || (a.title || "").toLowerCase().includes(filter.toLowerCase()) || (a.registration || "").toLowerCase().includes(filter.toLowerCase())), [meta, filter]);
  const crews = meta?.crews || [];

  const shiftDate = (d) => setDate((prev) => { const dt = new Date(prev + "T00:00:00"); dt.setDate(dt.getDate() + d); return dt.toISOString().slice(0, 10); });

  const applyTravel = (startMin, siteDur) => {
    const travelMins = Math.round((Number(travelHrs) || 0) * 60);
    if (!travelMins || travelExp === "maintain") return { start: startMin, duration: siteDur };
    // expand: total block grows by travel time
    if (travelCalc === "prepend") return { start: Math.max(0, startMin - travelMins), duration: siteDur + travelMins };
    return { start: startMin, duration: siteDur + travelMins }; // append
  };

  const buildPayload = () => {
    const base = { date, work_type: workType || (meta?.work_types || [])[0], shift_status: shiftStatus };
    if (assignType === "crew" && crewId) base.crew_id = crewId;
    base.task_legacy_id = linkedTask?.legacy_id || null;
    if (tab === "basic" && placement) {
      const adj = applyTravel(placement.start, placement.duration);
      return { ...base, vehicle_legacy_id: placement.vehicle_legacy_id, start_time: hhmm(adj.start), duration_mins: adj.duration,
        chargeable_travel_hours: Number(travelHrs) || 0 };
    }
    const startMin = toMins(startTime);
    const siteDur = Math.max(0, toMins(endTime) - startMin);
    const adj = applyTravel(startMin, siteDur);
    return { ...base, vehicle_legacy_id: vehicle, staff: users, start_time: hhmm(adj.start), end_time: hhmm(adj.start + adj.duration), duration_mins: adj.duration,
      shift_pattern: patternName || null,
      customer_notify: notify, chargeable_on_site_hours: Number(onSiteHrs) || 0, chargeable_travel_hours: Number(travelHrs) || 0,
      infrastructure_asset: infra || null, additional_info: addInfo || null };
  };

  const applyPattern = (id) => {
    const p = (meta?.shift_patterns || []).find((x) => x.id === id);
    if (!p) return;
    setPatternName(p.name);
    if (p.work_type) setWorkType(p.work_type);
    if (p.start_time) setStartTime(p.start_time);
    if (p.end_time) setEndTime(p.end_time);
    if (p.duration_mins) setDuration(p.duration_mins);
  };

  const save = async () => {
    const payload = buildPayload();
    if (!payload.vehicle_legacy_id) { toast.error("Choose an asset (and place / set a time)"); return; }
    if (!payload.date) { toast.error("A date is required"); return; }
    setBusy(true);
    try {
      if (editing) { await api.put(`/scheduler/shifts/${shift.shift_id}`, payload); toast.success("Shift rescheduled"); }
      else { await api.post("/scheduler/shifts", payload); toast.success("Shift scheduled"); }
      onSaved();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not save shift"); }
    finally { setBusy(false); }
  };

  const placeAt = (vehId, startMin) => {
    if (assignType === "crew") { setPlacement(null); return; }
    setPlacement({ vehicle_legacy_id: vehId, start: startMin, duration });
  };

  return (
    <SlidePanel onClose={onClose} maxW="max-w-3xl" testId="shift-form">
      {/* header */}
      <div className="flex items-center justify-between gap-2 border-b border-slate-100 pl-5 pr-14 py-4">
        <h3 className="text-xl font-bold text-slate-900">{editing ? "Reschedule Shift" : "Schedule a Shift"}</h3>
        <div className="flex items-center gap-1.5">
          {!editing && (
            <div className="flex rounded-lg border border-slate-200 p-0.5">
              <button onClick={() => setAssignType("asset")} className={`px-2.5 py-1 text-xs rounded-md ${assignType === "asset" ? "bg-slate-100 font-semibold" : "text-slate-500"}`} data-testid="assign-asset">Assets</button>
              <button onClick={() => setAssignType("crew")} className={`px-2.5 py-1 text-xs rounded-md ${assignType === "crew" ? "bg-slate-100 font-semibold" : "text-slate-500"}`} data-testid="assign-crew">Crews</button>
            </div>
          )}
          <div className="flex rounded-lg border border-slate-200 p-0.5">
            <button onClick={() => setTab("basic")} className={`px-2.5 py-1 text-xs rounded-md ${tab === "basic" ? "bg-slate-100 font-semibold" : "text-slate-500"}`} data-testid="tab-basic">Basic</button>
            <button onClick={() => setTab("advanced")} className={`px-2.5 py-1 text-xs rounded-md ${tab === "advanced" ? "bg-slate-100 font-semibold" : "text-slate-500"}`} data-testid="tab-advanced">Advanced</button>
          </div>
          <div className="relative">
            <button onClick={() => { setShowKey((v) => !v); setShowSettings(false); }} className="flex items-center gap-1 rounded-lg border border-slate-200 px-2.5 py-1.5 text-xs text-slate-600 hover:bg-slate-50" data-testid="shift-form-key"><KeyRound className="w-3.5 h-3.5" /> Key</button>
            {showKey && <SchedulerKey onClose={() => setShowKey(false)} />}
          </div>
          <button onClick={() => { setShowSettings(true); setShowKey(false); }} className="rounded-lg border border-slate-200 p-1.5 text-slate-600 hover:bg-slate-50" data-testid="shift-form-settings"><Settings className="w-4 h-4" /></button>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto p-5">
        {!meta ? <div className="p-10 text-center text-slate-400"><Loader2 className="w-5 h-5 animate-spin inline" /></div> : (
          <>
            {/* controls row (both modes) */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4">
              <div>
                <label className="text-xs font-medium text-slate-500">Filter Resources</label>
                <div className="relative mt-1"><Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input value={filter} onChange={(e) => setFilter(e.target.value)} className="pl-8" placeholder="Search" data-testid="shift-filter" /></div>
              </div>
              <div>
                <label className="text-xs font-medium text-slate-500">Date</label>
                <div className="mt-1 flex items-center rounded-lg border border-slate-300 bg-slate-50">
                  <button type="button" onClick={() => shiftDate(-1)} className="px-2 py-2 text-slate-500 hover:text-indigo-600" data-testid="shift-date-prev"><ChevronLeft className="w-4 h-4" /></button>
                  <Popover>
                    <PopoverTrigger asChild>
                      <button type="button" className="flex-1 min-w-0 bg-transparent text-center text-sm text-slate-700 py-2 hover:text-indigo-600 focus:outline-none cursor-pointer" data-testid="shift-date-trigger">{fmtDdmmyyyy(date) || "Select date"}</button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="center">
                      <Calendar mode="single" selected={date ? new Date(date + "T00:00:00") : undefined} onSelect={(d) => { if (d) setDate(toISO(d)); }} initialFocus />
                    </PopoverContent>
                  </Popover>
                  <button type="button" onClick={() => shiftDate(1)} className="px-2 py-2 text-slate-500 hover:text-indigo-600" data-testid="shift-date-next"><ChevronRight className="w-4 h-4" /></button>
                </div>
              </div>
              <div>
                <label className="text-xs font-medium text-slate-500">Duration</label>
                <div className="relative mt-1">
                  <select value={duration} onChange={(e) => setDuration(Number(e.target.value))} className={selCls} data-testid="shift-duration">
                    {(meta.durations || []).map((d) => <option key={d} value={d}>{d < 60 ? `${d} Min` : `${d / 60} Hour${d >= 120 ? "s" : ""}`}</option>)}
                  </select><ChevronDown className="pointer-events-none absolute right-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                </div>
              </div>
              <div>
                <label className="text-xs font-medium text-slate-500">Work Type</label>
                <div className="relative mt-1">
                  <select value={workType} onChange={(e) => setWorkType(e.target.value)} className={selCls} data-testid="shift-work-type">
                    <option value="">Please select…</option>
                    {(meta.work_types || []).map((w) => <option key={w} value={w}>{w}</option>)}
                  </select><ChevronDown className="pointer-events-none absolute right-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                </div>
              </div>
            </div>

            <div className="mb-4">
              <label className="text-xs font-medium text-slate-500">Linked Job <span className="text-slate-400 font-normal">(optional)</span></label>
              {linkedTask ? (
                <div className="mt-1 flex items-center justify-between gap-2 rounded-lg border border-indigo-200 bg-indigo-50 px-3 py-2" data-testid="shift-linked-job">
                  <span className="text-sm text-indigo-800 truncate">#{linkedTask.legacy_id}{linkedTask.title ? ` · ${linkedTask.title}` : ""}</span>
                  <button type="button" onClick={() => { setLinkedTask(null); setJobQuery(""); }} className="text-indigo-400 hover:text-indigo-700 shrink-0" data-testid="shift-linked-job-clear"><X className="w-4 h-4" /></button>
                </div>
              ) : (
                <div className="relative mt-1">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input value={jobQuery} onChange={(e) => setJobQuery(e.target.value)} placeholder="Search a job by ID, title, postcode…" className="pl-9" data-testid="shift-job-search" />
                  {jobQuery.trim().length >= 2 && (
                    <div className="absolute z-30 mt-1 w-full max-h-64 overflow-y-auto rounded-lg border border-slate-200 bg-white shadow-xl" data-testid="shift-job-results">
                      {jobSearching ? <div className="px-3 py-3 text-sm text-slate-400 flex items-center gap-2"><Loader2 className="w-3.5 h-3.5 animate-spin" /> Searching…</div>
                        : jobResults.length === 0 ? <div className="px-3 py-3 text-sm text-slate-400">No jobs found.</div>
                        : jobResults.map((j) => (
                          <button key={j.legacy_id} type="button" onClick={() => { setLinkedTask({ legacy_id: j.legacy_id, title: j.title || "" }); if (!workType && j.task_type) setWorkType(j.task_type); }}
                            className="block w-full text-left px-3 py-2 hover:bg-slate-50" data-testid={`shift-job-pick-${j.legacy_id}`}>
                            <span className="text-sm font-medium text-slate-800">#{j.legacy_id} · {j.title || "Untitled"}</span>
                            <span className="block text-[11px] text-slate-400">{[j.company, j.venue_postcode].filter(Boolean).join(" · ") || "—"}</span>
                          </button>
                        ))}
                    </div>
                  )}
                </div>
              )}
            </div>

            {tab === "basic" ? (
              <div className="rounded-xl border border-slate-200 overflow-hidden">
                <div className="flex items-center border-b border-slate-100 bg-slate-50/60 pl-40 pr-2">
                  {HOUR_LABELS.filter((_, i) => i % 1 === 0).map((l, i) => <div key={i} className="flex-1 py-1.5 text-center text-[10px] text-slate-400">{l}</div>)}
                </div>
                {!workType ? (
                  <div className="py-12 text-center text-sm text-slate-400" data-testid="basic-select-worktype">Please Select A Work Type</div>
                ) : assignType === "crew" ? (
                  <div className="py-10 text-center" data-testid="basic-crews">
                    {crews.length === 0 ? <p className="text-sm text-slate-400">No crews defined. Create crews first.</p> :
                      crews.map((cr) => (
                        <button key={cr.id} onClick={() => { setCrewId(cr.id); setUsers((cr.users || []).map((u) => u.name)); setTab("advanced"); }}
                          className="m-1 rounded-lg bg-indigo-600 px-4 py-2 text-sm font-medium text-white hover:bg-indigo-700" data-testid={`basic-crew-${cr.id}`}>{cr.title}</button>
                      ))}
                  </div>
                ) : (
                  <div>
                    {assets.length === 0 ? <div className="py-10 text-center text-sm text-slate-400">No assets match.</div> :
                      assets.map((a) => (
                        <TimelineRow key={a.legacy_id} label={a.title} sub={a.registration}
                          existing={dayShifts.filter((s) => s.vehicle_legacy_id === a.legacy_id)}
                          placement={placement && placement.vehicle_legacy_id === a.legacy_id ? placement : null}
                          onPlace={(m) => placeAt(a.legacy_id, m)} step={step} />
                      ))}
                    {placement && <div className="border-t border-slate-100 bg-indigo-50/40 px-4 py-2 text-xs text-indigo-700" data-testid="placement-summary">
                      Placed on <b>{assets.find((a) => a.legacy_id === placement.vehicle_legacy_id)?.title}</b> at {hhmm(placement.start)}–{hhmm(placement.start + placement.duration)}</div>}
                  </div>
                )}
              </div>
            ) : (
              <div className="space-y-4">
                <div>
                  <label className="text-xs font-medium text-slate-500">Work Type</label>
                  <div className="relative mt-1"><select value={workType} onChange={(e) => setWorkType(e.target.value)} className={selCls} data-testid="adv-work-type">
                    <option value="">Please select…</option>{(meta.work_types || []).map((w) => <option key={w} value={w}>{w}</option>)}</select>
                    <ChevronDown className="pointer-events-none absolute right-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" /></div>
                </div>
                {assignType === "crew" && (
                  <div><label className="text-xs font-medium text-slate-500">Crew</label>
                    <div className="relative mt-1"><select value={crewId} onChange={(e) => { setCrewId(e.target.value); const cr = crews.find((c) => c.id === e.target.value); if (cr) setUsers((cr.users || []).map((u) => u.name)); }} className={selCls} data-testid="adv-crew">
                      <option value="">Please select…</option>{crews.map((c) => <option key={c.id} value={c.id}>{c.title}</option>)}</select>
                      <ChevronDown className="pointer-events-none absolute right-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" /></div></div>
                )}
                <div><label className="text-xs font-medium text-slate-500">Asset</label>
                  <div className="relative mt-1"><select value={vehicle} onChange={(e) => setVehicle(e.target.value)} className={selCls} data-testid="adv-asset">
                    <option value="">Please select…</option>{(meta.assets || []).map((a) => <option key={a.legacy_id} value={a.legacy_id}>{a.title}</option>)}</select>
                    <ChevronDown className="pointer-events-none absolute right-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" /></div></div>
                <div><label className="text-xs font-medium text-slate-500">Engineers</label>
                  <div className="mt-1"><UsersPicker options={meta.staff || []} value={users} onChange={setUsers} /></div></div>
                <div>
                  <div className="flex items-center justify-between"><label className="text-xs font-medium text-slate-500">Shift Patterns</label>
                    <button type="button" onClick={() => setShowPatterns(true)} className="text-[11px] text-indigo-600 hover:underline" data-testid="manage-patterns">Manage</button></div>
                  <div className="relative mt-1"><select onChange={(e) => applyPattern(e.target.value)} className={selCls} data-testid="adv-pattern">
                    <option value="">Please select…</option>{(meta.shift_patterns || []).map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}</select>
                    <ChevronDown className="pointer-events-none absolute right-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" /></div>
                </div>
                <div><label className="text-xs font-medium text-slate-500">Status</label>
                  <div className="relative mt-1"><select value={shiftStatus} onChange={(e) => setShiftStatus(e.target.value)} className={selCls} data-testid="adv-status">
                    {SHIFT_STATUS_ORDER.map((k) => <option key={k} value={k}>{SHIFT_STATUS_META[k].label}</option>)}</select>
                    <ChevronDown className="pointer-events-none absolute right-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" /></div></div>
                <div className="grid grid-cols-2 gap-3">
                  <div><label className="text-xs font-medium text-slate-500">Start Time</label>
                    <Input type="time" value={startTime} onChange={(e) => setStartTime(e.target.value)} className="mt-1" data-testid="adv-start" /></div>
                  <div><label className="text-xs font-medium text-slate-500">End Time</label>
                    <Input type="time" value={endTime} onChange={(e) => setEndTime(e.target.value)} className="mt-1" data-testid="adv-end" /></div>
                </div>
                <Collapsible title="Customer Notifications" testId="coll-notify">
                  <label className="flex items-center gap-2 text-sm text-slate-600"><input type="checkbox" checked={notify} onChange={(e) => setNotify(e.target.checked)} data-testid="adv-notify" /> Notify the customer of this shift</label>
                </Collapsible>
                <Collapsible title="Chargeable Hours" testId="coll-charge">
                  <div><label className="text-xs font-medium text-slate-500">Chargeable On Site Hours</label>
                    <Input type="number" step="0.25" value={onSiteHrs} onChange={(e) => setOnSiteHrs(e.target.value)} className="mt-1" placeholder="0.00" data-testid="adv-onsite" /></div>
                  <div><label className="text-xs font-medium text-slate-500">Chargeable Travel Hours</label>
                    <Input type="number" step="0.25" value={travelHrs} onChange={(e) => setTravelHrs(e.target.value)} className="mt-1" placeholder="0.00" data-testid="adv-travel" /></div>
                </Collapsible>
                <Collapsible title="Infrastructure Asset" testId="coll-infra">
                  <Input value={infra} onChange={(e) => setInfra(e.target.value)} placeholder="e.g. Crane, Generator" data-testid="adv-infra" />
                </Collapsible>
                <Collapsible title="Additional Information" testId="coll-addinfo">
                  <textarea value={addInfo} onChange={(e) => setAddInfo(e.target.value)} rows={3} className="w-full rounded-lg border border-slate-300 bg-slate-50 p-2 text-sm" placeholder="Notes for this shift" data-testid="adv-addinfo" />
                </Collapsible>
              </div>
            )}
          </>
        )}
      </div>

      <div className="flex justify-end gap-2 border-t border-slate-100 px-5 py-4">
        <Button variant="outline" onClick={onClose} data-testid="shift-form-cancel">Cancel</Button>
        <Button className="bg-indigo-600 hover:bg-indigo-700 text-white" disabled={busy} onClick={save} data-testid="shift-form-save">{busy && <Loader2 className="w-4 h-4 animate-spin mr-1" />}Save</Button>
      </div>

      {showSettings && <SettingsMenu assignType={assignType} setAssignType={setAssignType}
        step={step} setStep={setStep} travelCalc={travelCalc} setTravelCalc={setTravelCalc}
        travelExp={travelExp} setTravelExp={setTravelExp}
        onSave={() => { saveSchedulerPrefs(); setShowSettings(false); }} onClose={() => setShowSettings(false)} />}
      {showPatterns && <ShiftPatternsManager workTypes={meta?.work_types || []} onClose={() => setShowPatterns(false)} onChanged={() => api.get("/scheduler/meta").then((r) => setMeta(r.data)).catch(() => {})} />}
    </SlidePanel>
  );
}

function OptCard({ selected, title, desc, onClick, testId }) {
  return (
    <button onClick={onClick} data-testid={testId}
      className={`relative rounded-xl border p-4 text-left ${selected ? "border-indigo-500 ring-1 ring-indigo-200" : "border-slate-200 hover:border-slate-300"}`}>
      {selected && <span className="absolute right-3 top-3 flex h-5 w-5 items-center justify-center rounded-full bg-indigo-600 text-white text-[10px]">✓</span>}
      <div className="text-sm font-semibold text-slate-800">{title}</div>
      <div className="mt-1 text-xs text-slate-500">{desc}</div>
    </button>
  );
}

function SettingsMenu({ assignType, setAssignType, step, setStep, travelCalc, setTravelCalc, travelExp, setTravelExp, onSave, onClose }) {
  return (
    <SlidePanel onClose={onClose} maxW="max-w-3xl" testId="assignment-settings">
      <div className="flex items-center justify-between border-b border-slate-100 pl-5 pr-14 py-4">
        <h3 className="text-xl font-bold text-slate-900">Assignment Group Form Menu</h3>
      </div>
      <div className="flex-1 overflow-y-auto p-5 space-y-6">
        <section>
          <div className="text-xs font-bold uppercase tracking-wide text-slate-500 mb-2">Assignment Type</div>
          <div className="grid grid-cols-2 gap-3">
            <OptCard selected={assignType === "asset"} title="Asset" desc="Choose an Asset and assign Engineers to the shift separately" onClick={() => setAssignType("asset")} testId="set-asset" />
            <OptCard selected={assignType === "crew"} title="Crew" desc="Assign Assets and Engineers to a shift together as a predefined Crew" onClick={() => setAssignType("crew")} testId="set-crew" />
          </div>
        </section>
        <section>
          <div className="text-xs font-bold uppercase tracking-wide text-slate-500 mb-2">Step Interval</div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            {[5, 10, 15, 30].map((s) => <OptCard key={s} selected={step === s} title={`${s} Mins`} desc={`Times will snap to ${s} minute intervals`} onClick={() => setStep(s)} testId={`set-step-${s}`} />)}
          </div>
        </section>
        <section>
          <div className="text-xs font-bold uppercase tracking-wide text-slate-500 mb-2">Travel Calculation Mode</div>
          <div className="grid grid-cols-2 gap-3">
            <OptCard selected={travelCalc === "prepend"} title="Prepend" desc="Travel time will be added before the shift site time" onClick={() => setTravelCalc("prepend")} testId="set-prepend" />
            <OptCard selected={travelCalc === "append"} title="Append" desc="Travel time will be added after the current shift site time" onClick={() => setTravelCalc("append")} testId="set-append" />
          </div>
        </section>
        <section>
          <div className="text-xs font-bold uppercase tracking-wide text-slate-500 mb-2">Travel Expansion Mode</div>
          <div className="grid grid-cols-2 gap-3">
            <OptCard selected={travelExp === "expand"} title="Expand" desc="Travel time will be added to the existing site duration you placed" onClick={() => setTravelExp("expand")} testId="set-expand" />
            <OptCard selected={travelExp === "maintain"} title="Maintain" desc="Overall shift duration is maintained; travel time reduces the site duration" onClick={() => setTravelExp("maintain")} testId="set-maintain" />
          </div>
        </section>
      </div>
      <div className="flex justify-end gap-2 border-t border-slate-100 px-5 py-4">
        <Button variant="outline" onClick={onClose}>Cancel</Button>
        <Button className="bg-indigo-600 hover:bg-indigo-700 text-white" onClick={onSave} data-testid="settings-save">Save</Button>
      </div>
    </SlidePanel>
  );
}
